import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { usePost } from '../API';
import type { Payment, PaymentBatchRecipient } from '@aidkitorg/aidkit/lib/admin_panel/payments';
import { logAdminPanelError } from '../utils/adminPanel.utils';

type PaymentsContextType = {
  payments: Array<Payment>
  enabledPayments: number,
  paymentsDataTimestamp?: number,

  isPaymentsLoading: boolean,
  getIsBatchLoading: (batchTargetField: string) => boolean,
  
  fetchPaymentsOverview: (force?: boolean) => void,
  fetchRecipientDetailsByBatch: (batchTargetField: string, force?: boolean) => void,
  getBatchRecipientDetails: (batchTargetField: string) => Array<PaymentBatchRecipient>,
  getBatchTimestamp: (batchTargetField: string) => number | undefined,
  getBatchNumRecipients: (batchTargetField: string) => number,
  getBatchName: (batchTargetField: string) => string,
  getBatchNumFailedPayments: (batchTargetField: string) => number | undefined,
}

type PaymentBatch = {
  batchName: string,
  recipients: Array<PaymentBatchRecipient>,
  numFailedPayments: number,
  timestamp?: number,
  expiresAt?: number,
}

type PaymentsBatchRef = {
  [batchTargetField: string]: PaymentBatch
}

type PaymentsOverviewRef = {
  payments?: Array<Payment>,
  timestamp?: number,
  expiresAt?: number,
}

type PaymentsProviderProps = {
  children: React.ReactNode
}

const PaymentsContext = createContext<PaymentsContextType>({
  payments: [],
  enabledPayments: 0,

  isPaymentsLoading: false,
  getIsBatchLoading: () => false,

  fetchPaymentsOverview: async (_force?: boolean) => {},
  fetchRecipientDetailsByBatch: async (_batch: string) => {},
  getBatchRecipientDetails: () => [],
  getBatchTimestamp: () => undefined,
  getBatchNumRecipients: () => 0,
  getBatchName: () => '',
  getBatchNumFailedPayments: () => 0,
})

export const usePayments = () => {
  const context = useContext(PaymentsContext)
  if (!context) {
    throw new Error("usePayments must be used within a PaymentsProvider")
  }

  return context;
}

export const PaymentsProvider: React.FC<PaymentsProviderProps> = ({ children }) => {
  const [payments, setPayments] = useState<Array<Payment>>([])
  const [enabledPayments, setEnabledPayments] = useState(0)
  const [paymentsDataTimestamp, setPaymentsDataTimestamp] = useState<number>()

  const [isPaymentsLoading, setIsPaymentsLoading] = useState(false);
  const [isBatchLoading, setIsBatchLoading] = useState<{ [batchTargetField: string]: boolean }>({})

  const [paymentsByBatch, setPaymentsByBatch] = useState<
    { [batchTargetField: string]: PaymentBatch }
  >({})

  const paymentByBatchRef = useRef<PaymentsBatchRef>({});
  const paymentsRef = useRef<PaymentsOverviewRef>({})

  const getPaymentsData = usePost("/api/admin_panel/payments")
  const getPaymentBatchData = usePost("/api/admin_panel/payment_details")

  const fetchPaymentsOverview = useCallback(async (force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force && 
      paymentsRef.current.payments && 
      (paymentsRef.current.expiresAt && now < paymentsRef.current.expiresAt)) 
    {
      return
    }

    setIsPaymentsLoading(true)
    const result = await getPaymentsData({ force })
    setIsPaymentsLoading(false)

    if (result && result.success && result.data) {
      paymentsRef.current = { payments: result.data.payments, timestamp: result.data.timestamp, expiresAt: result.data.expiresAt }

      setEnabledPayments(result.data.totalEnabledPayments)
      setPayments(result.data.payments)
      setPaymentsDataTimestamp(result.data.timestamp)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getPaymentsData])

  const fetchRecipientDetailsByBatch = useCallback(async (batchTargetField: string, force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    const entry = paymentByBatchRef.current[batchTargetField]
    if (!force &&
        entry &&
       (entry.expiresAt !== undefined && now < entry.expiresAt))
    {
      return
    }

    setIsBatchLoading(prev => ({ ...prev, [batchTargetField]: true }))
    const result = await getPaymentBatchData({ batchTargetField, force })
    setIsBatchLoading(prev => ({ ...prev, [batchTargetField]: false }))

    if (result && result.success && result.data !== null) {
      const paymentBatchDetails = {
        batchName: result.data.batchName,
        recipients: result.data.recipients, 
        timestamp: result.data.timestamp,
        numFailedPayments: result.data.numFailedPayments
      }
      paymentByBatchRef.current[batchTargetField] = { ...paymentBatchDetails, expiresAt: result.data.expiresAt};

      setPaymentsByBatch((v) => (
        {...v,
          [batchTargetField]: paymentBatchDetails
        }
      ))
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getPaymentBatchData])

  const getBatchRecipientDetails = useCallback((batchTargetField: string) => {
    return paymentsByBatch[batchTargetField]?.recipients || []
  }, [paymentsByBatch])

  const getBatchTimestamp = useCallback((batchTargetField: string) => {
    return paymentsByBatch[batchTargetField]?.timestamp
  }, [paymentsByBatch])

  const getBatchNumRecipients = useCallback((batchTargetField: string) => {
    return paymentsByBatch[batchTargetField]?.recipients.length || 0
  }, [paymentsByBatch])

  const getBatchName = useCallback((batchTargetField: string) => {
    return paymentsByBatch[batchTargetField]?.batchName || ''
  }, [paymentsByBatch])

  const getBatchNumFailedPayments = useCallback((batchTargetField: string) => {
    return paymentsByBatch[batchTargetField]?.numFailedPayments
  }, [paymentsByBatch])

  const getIsBatchLoading = useCallback((batchTargetField: string) => {
    return isBatchLoading[batchTargetField]
  }, [isBatchLoading])

  const value = {
    payments,
    enabledPayments,
    paymentsDataTimestamp,

    isPaymentsLoading,
    getIsBatchLoading,

    fetchPaymentsOverview,
    fetchRecipientDetailsByBatch,
    getBatchRecipientDetails,
    getBatchTimestamp,
    getBatchNumRecipients,
    getBatchName,
    getBatchNumFailedPayments
  }

  return (
    <PaymentsContext.Provider value={ value }>
      {children}
    </PaymentsContext.Provider>
  )
}
